<!-- eslint-disable vue/multi-word-component-names -->
<script setup lang="ts">
  import {computed, ref, onBeforeMount, useAttrs} from 'vue';
  import {getComponentConfig} from '@core/utils/getComponentConfig';
  import Icon from '@core/components/Icon.vue';

  defineOptions({
    inheritAttrs: false,
  });

  interface Props {
    variant?: 'elevated' | 'flat' | 'outlined' | 'danger' | 'warning';
    rounded?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | 'full' | 'none';
    loading?: boolean;
    disabled?: boolean;
    class?: string;
  }

  const props = withDefaults(defineProps<Props>(), {
    variant: 'outlined',
    rounded: 'lg',
    loading: false,
    disabled: false,
    class: '',
  });

  const attrs = useAttrs();

  /** Fallthrough attrs without `class` / `style` so they cannot override `cardClasses`. */
  const fallthroughAttrs = computed(() => {
    const raw = attrs as Record<string, unknown>;
    const {class: _c, style: _s, ...rest} = raw;
    return rest;
  });

  const defaultConfig = {
    base: 'overflow-hidden transition-all duration-200',
    variants: {
      elevated:
        'bg-white dark:bg-zinc-900! shadow-sm border border-gray-200 dark:border-white/10 transition-all duration-300',
      flat: 'bg-white dark:bg-zinc-900! border-0',
      outlined:
        'bg-white dark:bg-zinc-900! rounded-xl border border-gray-200 dark:border-white/10',
      danger:
        'bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-900/50',
      warning:
        'bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800/50',
    },
    rounded: {
      none: 'rounded-none',
      sm: 'rounded-sm',
      md: 'rounded-md',
      lg: 'rounded-lg',
      xl: 'rounded-xl',
      '2xl': 'rounded-2xl',
      full: 'rounded-full',
    },
    loadingOverlay:
      'absolute inset-0 z-10 flex items-center justify-center bg-white/50 dark:bg-black/50 backdrop-blur-[1px]',
    title: {
      base: 'card-title flex items-center px-6 pt-6 pb-2 last:pb-6 text-xl font-bold text-gray-900 dark:text-white',
    },
    content: {
      base: 'card-content px-6 pt-2 pb-6 first:pt-6 text-base dark:text-gray-300',
    },
    actions: {
      base: 'card-actions flex items-center justify-end px-6 py-4 gap-2 border-t border-gray-100 dark:border-gray-800',
    },
  };

  const config = ref(defaultConfig);

  onBeforeMount(async () => {
    config.value = await getComponentConfig('Card', defaultConfig);
  });

  // Fallthrough listeners / attrs (excluding class) bind to the single root <div>.
  const isClickable = computed(() =>
    Boolean((attrs as {onClick?: unknown}).onClick),
  );

  /** Clickable surface: fallthrough `onClick` and not inert via props. */
  const isInteractiveSurface = computed(
    () => isClickable.value && !props.disabled && !props.loading,
  );

  const handleCardKeyActivate = (e: KeyboardEvent) => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    e.preventDefault();
    (e.currentTarget as HTMLElement).click();
  };

  const cardKeyboardListeners = computed(() =>
    isInteractiveSurface.value ? {keydown: handleCardKeyActivate} : {},
  );

  const cardClasses = computed(() => {
    const classes = [
      'card',
      config.value.rounded[props.rounded] || config.value.rounded.lg,
      config.value.base,
      config.value.variants[props.variant] || config.value.variants.elevated,
      isClickable.value
        ? `cursor-pointer hover:shadow-md focus-visible:outline-none
          focus-visible:ring-2 focus-visible:ring-brand-primary
          focus-visible:ring-offset-2 dark:focus-visible:ring-offset-zinc-900`
        : '',
      props.disabled ? 'opacity-50 pointer-events-none' : '',
      props.class,
      ...(props.variant === 'outlined' || props.variant === 'elevated'
        ? []
        : // BEM hook for danger/warning (unnamed variant tokens)
          [`variant`]),
    ];
    return classes.filter(Boolean).join(' ');
  });
</script>

<template>
  <div
    v-bind="fallthroughAttrs"
    :class="cardClasses"
    :role="isInteractiveSurface ? 'button' : undefined"
    :tabindex="isInteractiveSurface ? 0 : undefined"
    :aria-disabled="disabled ? true : undefined"
    v-on="cardKeyboardListeners">
    <!-- Loading Overlay -->
    <div
      v-if="loading"
      :class="['card__loading-overlay', config.loadingOverlay]">
      <Icon
        name="loader"
        class="animate-spin text-brand-primary"
        :size="32" />
    </div>

    <!-- Content -->
    <div class="card__body relative flex min-h-0 flex-1 flex-col">
      <slot />
    </div>
  </div>
</template>
